"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "./button";
import { Input } from "./input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from "./modal";

/* ── Types ──────────────────────────────────────────────────── */

type ConfirmVariant = "default" | "danger";

interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
}

interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
  inputLabel?: string;
  /** When true, pressing confirm with an empty value still resolves to "". */
  allowEmpty?: boolean;
}

type DialogState =
  | {
      kind: "confirm";
      options: ConfirmOptions;
      resolve: (v: boolean) => void;
    }
  | {
      kind: "prompt";
      options: PromptOptions;
      resolve: (v: string | null) => void;
    };

interface ConfirmContextValue {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  prompt: (opts: PromptOptions) => Promise<string | null>;
}

/* ── Context ────────────────────────────────────────────────── */

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used inside <ConfirmProvider>");
  return ctx.confirm;
}

export function usePrompt() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("usePrompt must be used inside <ConfirmProvider>");
  return ctx.prompt;
}

/* ── Provider ───────────────────────────────────────────────── */

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setState({ kind: "confirm", options, resolve });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      setState({ kind: "prompt", options, resolve });
    });
  }, []);

  const handleClose = useCallback(
    (result: boolean | string | null) => {
      if (!state) return;
      if (state.kind === "confirm") {
        state.resolve(result === true);
      } else {
        state.resolve(typeof result === "string" ? result : null);
      }
      setState(null);
    },
    [state],
  );

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {state ? (
        state.kind === "confirm" ? (
          <ConfirmDialog state={state} onClose={handleClose} />
        ) : (
          <PromptDialog state={state} onClose={handleClose} />
        )
      ) : null}
    </ConfirmContext.Provider>
  );
}

/* ── Dialogs ────────────────────────────────────────────────── */

function ConfirmDialog({
  state,
  onClose,
}: {
  state: Extract<DialogState, { kind: "confirm" }>;
  onClose: (result: boolean | null) => void;
}) {
  const {
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "default",
  } = state.options;

  return (
    <Modal open onClose={() => onClose(false)}>
      <ModalHeader onClose={() => onClose(false)}>
        <ModalTitle>{title}</ModalTitle>
        {description ? <ModalDescription>{description}</ModalDescription> : null}
      </ModalHeader>
      <ModalBody />
      <ModalFooter>
        <Button variant="ghost" onClick={() => onClose(false)}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === "danger" ? "destructive" : "primary"}
          onClick={() => onClose(true)}
          autoFocus
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function PromptDialog({
  state,
  onClose,
}: {
  state: Extract<DialogState, { kind: "prompt" }>;
  onClose: (result: string | null) => void;
}) {
  const {
    title,
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "default",
    placeholder,
    defaultValue = "",
    inputLabel,
    allowEmpty = true,
  } = state.options;

  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus on mount
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  const submit = () => {
    const trimmed = value.trim();
    if (!allowEmpty && trimmed === "") return;
    onClose(value);
  };

  return (
    <Modal open onClose={() => onClose(null)}>
      <ModalHeader onClose={() => onClose(null)}>
        <ModalTitle>{title}</ModalTitle>
        {description ? <ModalDescription>{description}</ModalDescription> : null}
      </ModalHeader>
      <ModalBody>
        <Input
          ref={inputRef}
          label={inputLabel}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={() => onClose(null)}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant === "danger" ? "destructive" : "primary"}
          onClick={submit}
          disabled={!allowEmpty && value.trim() === ""}
        >
          {confirmLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
